import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { CalEvent } from "../../api/types";
import { HOLIDAY_COLOR } from "../../lib/colors";
import {
  WEEKDAYS_SHORT,
  absMinutes,
  addDays,
  datePart,
  diffDays,
  formatTime,
  makeDateTime,
  minutesOf,
  nowIn,
  parts,
  weekday
} from "../../lib/date";
import { newDraft } from "../../lib/draft";
import { layoutBars, layoutTimed } from "../../lib/layout";
import { anchorOf, useUi } from "../../state/ui";
import { endDay, isBarEvent, isPast, useHolidayPopover, useMoveEvent, useVisibleEvents } from "./common";
import "./TimeGridView.css";

const HOUR_PX = 48;
const PX_PER_MIN = HOUR_PX / 60;
const SNAP = 15;
const DRAG_THRESHOLD = 4;

type Drag =
  | { mode: "create"; dayIndex: number; anchorMin: number; curMin: number; moved: boolean; startX: number; startY: number }
  | { mode: "move"; event: CalEvent; grabMin: number; dayIndex: number; curDayIndex: number; curStart: number; moved: boolean; startX: number; startY: number; el: HTMLElement }
  | { mode: "resize"; event: CalEvent; dayIndex: number; curEnd: number; moved: boolean; startX: number; startY: number };

const snap = (m: number) => Math.round(m / SNAP) * SNAP;

export function TimeGridView() {
  const ui = useUi();
  const days = ui.visibleDays;
  const from = days[0];
  const to = addDays(days[days.length - 1], 1);
  const { events, holidays } = useVisibleEvents(from, to);
  const moveEvent = useMoveEvent();
  const holidayPopover = useHolidayPopover();
  const [now, setNow] = useState(() => nowIn(ui.me.timezone));
  const [drag, setDrag] = useState<Drag | null>(null);
  /** All-day strip drag (HTML5 drag & drop, like the month view): grabbed and hovered day columns. */
  const [barDrag, setBarDrag] = useState<{ key: string; grab: number; over: number } | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const colsRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setInterval(() => setNow(nowIn(ui.me.timezone)), 30_000);
    return () => clearInterval(t);
  }, [ui.me.timezone]);

  // Keep the header columns aligned with the scrollbar-narrowed body.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.parentElement?.style.setProperty("--scrollbar", `${el.offsetWidth - el.clientWidth}px`);
  });

  // Scroll near the current time (or 07:00) when the view opens.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const target = days.includes(now.date) ? now.minutes - 90 : 7 * 60;
    el.scrollTop = Math.max(0, target * PX_PER_MIN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.view]);

  const barEvents = events.filter(isBarEvent);
  const timedEvents = events.filter((e) => !isBarEvent(e));

  // --- All-day strip ------------------------------------------------------------------
  const bars = useMemo(() => {
    const items = [
      ...holidays.map((h) => ({ key: `holiday:${h.date}:${h.name}`, start: h.date, endExclusive: addDays(h.date, 1), order: 0 })),
      ...barEvents.map((e) => ({ key: e.key, start: datePart(e.start), endExclusive: endDay(e), order: 1 }))
    ];
    return layoutBars(items, from, days.length);
  }, [holidays, barEvents, from, days.length]);
  const barLevels = bars.reduce((m, b) => Math.max(m, b.level + 1), 0);

  // --- Timed events per day column -------------------------------------------------------
  const columns = useMemo(
    () =>
      days.map((day) => {
        const dayStart = absMinutes(`${day}T00:00`);
        const slices = timedEvents
          .filter((e) => datePart(e.start) <= day && e.end > `${day}T00:00`)
          .map((e) => ({ key: e.key, start: absMinutes(e.start) - dayStart, end: absMinutes(e.end) - dayStart }));
        return layoutTimed(slices);
      }),
    [days, timedEvents]
  );
  const byKey = useMemo(() => new Map(events.map((e) => [e.key, e])), [events]);

  // --- Pointer interactions --------------------------------------------------------------
  const pointToSlot = (clientX: number, clientY: number) => {
    const rect = colsRef.current!.getBoundingClientRect();
    const dayIndex = Math.min(days.length - 1, Math.max(0, Math.floor(((clientX - rect.left) / rect.width) * days.length)));
    const minute = Math.min(1440, Math.max(0, (clientY - rect.top) / PX_PER_MIN));
    return { dayIndex, minute };
  };

  /** Day column under `clientX` within the all-day strip. */
  const columnAt = (area: HTMLElement, clientX: number) => {
    const rect = area.getBoundingClientRect();
    return Math.max(0, Math.min(days.length - 1, Math.floor(((clientX - rect.left) / rect.width) * days.length)));
  };

  const setDragBoth = (d: Drag | null) => {
    dragRef.current = d;
    setDrag(d);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || ui.overlay) return;
    const target = e.target as HTMLElement;
    const eventEl = target.closest<HTMLElement>("[data-event-key]");
    const { dayIndex, minute } = pointToSlot(e.clientX, e.clientY);
    const touch = e.pointerType === "touch";

    if (eventEl) {
      const event = byKey.get(eventEl.dataset.eventKey!);
      if (!event) return;
      if (touch) return; // taps are handled by onClick; no touch dragging
      if (target.closest(".tg-resize")) {
        setDragBoth({ mode: "resize", event, dayIndex, curEnd: absMinutes(event.end) - absMinutes(`${days[dayIndex]}T00:00`), moved: false, startX: e.clientX, startY: e.clientY });
      } else {
        const startInDay = absMinutes(event.start) - absMinutes(`${days[dayIndex]}T00:00`);
        setDragBoth({ mode: "move", event, grabMin: minute - startInDay, dayIndex, curDayIndex: dayIndex, curStart: startInDay, moved: false, startX: e.clientX, startY: e.clientY, el: eventEl });
      }
    } else {
      setDragBoth({ mode: "create", dayIndex, anchorMin: minute, curMin: minute, moved: false, startX: e.clientX, startY: e.clientY });
    }
    colsRef.current!.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const moved = d.moved || Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > DRAG_THRESHOLD;
    const { dayIndex, minute } = pointToSlot(e.clientX, e.clientY);
    if (d.mode === "create") setDragBoth({ ...d, curMin: minute, moved });
    else if (d.mode === "move") setDragBoth({ ...d, curDayIndex: dayIndex, curStart: snap(minute - d.grabMin), moved });
    else setDragBoth({ ...d, curEnd: Math.max(snap(minute), absMinutes(d.event.start) - absMinutes(`${days[d.dayIndex]}T00:00`) + SNAP), moved });
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    setDragBoth(null);
    if (!d) return;
    colsRef.current?.releasePointerCapture(e.pointerId);
    const day = days[d.dayIndex];

    if (d.mode === "create") {
      let startMin: number;
      let endMin: number;
      if (!d.moved) {
        startMin = Math.floor(d.anchorMin / 30) * 30;
        endMin = startMin + ui.me.settings.defaultDurationMinutes;
      } else {
        startMin = Math.floor(Math.min(d.anchorMin, d.curMin) / SNAP) * SNAP;
        endMin = Math.max(startMin + SNAP, Math.ceil(Math.max(d.anchorMin, d.curMin) / SNAP) * SNAP);
      }
      const draft = newDraft({ date: day, startMin, endMin, settings: ui.me.settings, calendars: ui.calendars });
      const col = colsRef.current!.children[d.dayIndex] as HTMLElement;
      const colRect = col.getBoundingClientRect();
      ui.setOverlay({
        kind: "quick",
        draft,
        anchor: { x: colRect.left, y: colRect.top + startMin * PX_PER_MIN, width: colRect.width, height: (endMin - startMin) * PX_PER_MIN }
      });
      return;
    }

    if (!d.moved) {
      if (d.mode === "move") ui.setOverlay({ kind: "details", event: d.event, anchor: anchorOf(d.el) });
      return;
    }
    if (d.mode === "move") {
      const oldStart = absMinutes(d.event.start);
      const newStart = absMinutes(`${days[d.curDayIndex]}T00:00`) + d.curStart;
      void moveEvent(d.event, { deltaMinutes: newStart - oldStart });
    } else {
      void moveEvent(d.event, { newEnd: makeDateTime(day, d.curEnd) });
    }
  };

  // --- Rendering helpers --------------------------------------------------------------------
  const quickDraft = ui.overlay?.kind === "quick" ? ui.overlay.draft : null;

  const renderBlock = (event: CalEvent, p: { top: number; height: number; left: number; width: number; z: number }, extra = "", ghost = false) => {
    const color = event.color;
    const short = p.height < 40;
    const range = `${formatTime(minutesOf(event.start))} – ${formatTime(minutesOf(event.end))}`;
    return (
      <div
        key={`${event.key}${ghost ? ":ghost" : ""}`}
        data-event-key={ghost ? undefined : event.key}
        role="button"
        tabIndex={ghost ? -1 : 0}
        aria-label={`${event.title}, ${range}`}
        className={`tg-event ev-surface${short ? " short" : ""}${extra}`}
        style={{
          top: p.top * PX_PER_MIN,
          height: Math.max(p.height * PX_PER_MIN - 1, 18),
          left: `calc(${p.left * 100}% + 1px)`,
          width: `calc(${p.width * 100}% - 6px)`,
          zIndex: p.z,
          ["--ev" as string]: color
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") ui.setOverlay({ kind: "details", event, anchor: anchorOf(e.currentTarget) });
        }}
        onClick={(e) => {
          // Touch taps (no pointer drag on touch devices).
          if ((e.nativeEvent as PointerEvent).pointerType === "touch") ui.setOverlay({ kind: "details", event, anchor: anchorOf(e.currentTarget) });
        }}
      >
        {short ? (
          <div className="tg-event-line">
            <strong>{event.title || "(sem título)"}</strong>, {formatTime(minutesOf(event.start))}
          </div>
        ) : (
          <>
            <div className="tg-event-title">{event.title || "(sem título)"}</div>
            <div className="tg-event-time">{range}</div>
            {event.location && p.height >= 75 && <div className="tg-event-time">{event.location}</div>}
          </>
        )}
        {!ghost && datePart(event.start) === datePart(event.end) && <div className="tg-resize" aria-hidden="true" />}
      </div>
    );
  };

  const offsetLabel = useMemo(() => {
    const name = new Intl.DateTimeFormat("en-US", { timeZone: ui.me.timezone, timeZoneName: "shortOffset" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    const m = name?.match(/GMT([+-])(\d+)(?::(\d+))?/);
    return m ? `GMT${m[1]}${m[2].padStart(2, "0")}${m[3] ? `:${m[3]}` : ""}` : "GMT";
  }, [ui.me.timezone]);

  return (
    <div className="tg" style={{ ["--days" as string]: days.length }}>
      <div className="tg-head">
        <div className="tg-gutter-head tz">{offsetLabel}</div>
        {days.map((day) => (
          <div key={day} className={`tg-day-head${day === now.date ? " today" : ""}`}>
            <span className="tg-dow">{WEEKDAYS_SHORT[weekday(day)].toUpperCase()}</span>
            <button className="tg-daynum" onClick={() => { ui.setDate(day); ui.setView("day"); }} aria-label={`Ver ${day}`}>
              {parts(day).d}
            </button>
          </div>
        ))}
      </div>

      <div className="tg-allday" style={{ ["--levels" as string]: Math.max(1, barLevels) }}>
        <div className="tg-gutter-head" />
        <div
          className="tg-allday-area"
          onDragOver={(e) => {
            if (!barDrag) return;
            e.preventDefault();
            const over = columnAt(e.currentTarget, e.clientX);
            if (over !== barDrag.over) setBarDrag({ ...barDrag, over });
          }}
          onDrop={(e) => {
            e.preventDefault();
            const d = barDrag;
            setBarDrag(null);
            const event = d && byKey.get(d.key);
            const delta = d ? diffDays(days[d.grab], days[columnAt(e.currentTarget, e.clientX)]) : 0;
            if (event && delta) void moveEvent(event, { deltaDays: delta });
          }}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("[data-bar]") || ui.overlay) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const idx = Math.floor(((e.clientX - rect.left) / rect.width) * days.length);
            const day = days[Math.max(0, Math.min(days.length - 1, idx))];
            ui.setOverlay({ kind: "quick", draft: newDraft({ date: day, allDay: true, settings: ui.me.settings, calendars: ui.calendars }), anchor: anchorOf(e.currentTarget, { x: e.clientX, y: e.clientY }) });
          }}
        >
          {days.map((d, i) => (
            <div key={d} className={`tg-allday-cell${barDrag?.over === i ? " drop" : ""}`} style={{ gridColumn: i + 1 }} />
          ))}
          {bars.map((b) => {
            const event = byKey.get(b.key);
            const holiday = event ? null : holidays.find((h) => `holiday:${h.date}:${h.name}` === b.key);
            const color = event?.color ?? HOLIDAY_COLOR;
            const past = event ? isPast(event, now.date, now.minutes) : false;
            return (
              <button
                key={b.key}
                data-bar
                className={`tg-bar ev-surface${past ? " past" : ""}${b.continuesBefore ? " cont-before" : ""}${b.continuesAfter ? " cont-after" : ""}${barDrag?.key === b.key ? " dragging" : ""}`}
                style={{ gridColumn: `${b.col + 1} / span ${b.span}`, gridRow: b.level + 1, ["--ev" as string]: color }}
                draggable={Boolean(event)}
                onDragStart={(e) => {
                  if (!event) return;
                  e.dataTransfer.setData("text/plain", b.key);
                  e.dataTransfer.effectAllowed = "move";
                  const area = e.currentTarget.closest<HTMLElement>(".tg-allday-area")!;
                  const grab = columnAt(area, e.clientX);
                  setBarDrag({ key: b.key, grab, over: grab });
                }}
                onDragEnd={() => setBarDrag(null)}
                onClick={(e) => {
                  if (event) ui.setOverlay({ kind: "details", event, anchor: anchorOf(e.currentTarget) });
                  else if (holiday) holidayPopover.open(holiday, anchorOf(e.currentTarget));
                }}
              >
                {event ? (event.allDay ? event.title : `${formatTime(minutesOf(event.start))} ${event.title}`) : holiday?.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tg-scroll" ref={scrollRef}>
        <div className="tg-body">
          <div className="tg-gutter">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="tg-hour-label">
                {h === 0 ? "" : `${String(h).padStart(2, "0")}:00`}
              </div>
            ))}
          </div>
          <div
            className={`tg-cols${drag?.moved ? " dragging" : ""}`}
            ref={colsRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => setDragBoth(null)}
          >
            {days.map((day, i) => (
              <div key={day} className={`tg-col${day === now.date ? " today" : ""}`} data-date={day}>
                {columns[i].map((p) => {
                  const event = byKey.get(p.key)!;
                  const hidden = drag && drag.mode !== "create" && drag.moved && drag.event.key === event.key;
                  return renderBlock(event, p, `${isPast(event, now.date, now.minutes) ? " past" : ""}${hidden ? " placeholder" : ""}`);
                })}

                {/* Ghost of the event being moved/resized. */}
                {drag && drag.moved && drag.mode === "move" && drag.curDayIndex === i &&
                  renderBlock(drag.event, { top: drag.curStart, height: absMinutes(drag.event.end) - absMinutes(drag.event.start), left: 0, width: 1, z: 50 }, " ghost", true)}
                {drag && drag.moved && drag.mode === "resize" && drag.dayIndex === i &&
                  renderBlock(
                    { ...drag.event, end: makeDateTime(day, drag.curEnd) },
                    { top: absMinutes(drag.event.start) - absMinutes(`${day}T00:00`), height: drag.curEnd - (absMinutes(drag.event.start) - absMinutes(`${day}T00:00`)), left: 0, width: 1, z: 50 },
                    " ghost",
                    true
                  )}

                {/* Selection while drag-creating, and the pending quick-create slot. */}
                {drag && drag.mode === "create" && drag.moved && drag.dayIndex === i && (() => {
                  const s = Math.floor(Math.min(drag.anchorMin, drag.curMin) / SNAP) * SNAP;
                  const en = Math.max(s + SNAP, Math.ceil(Math.max(drag.anchorMin, drag.curMin) / SNAP) * SNAP);
                  return (
                    <div className="tg-selection" style={{ top: s * PX_PER_MIN, height: (en - s) * PX_PER_MIN }}>
                      (sem título)
                      <br />
                      {formatTime(s)} – {formatTime(en)}
                    </div>
                  );
                })()}
                {quickDraft && !quickDraft.allDay && quickDraft.startDate === day && (
                  <div
                    className="tg-selection pending"
                    style={{
                      top: quickDraft.startMin * PX_PER_MIN,
                      height: Math.max(18, ((quickDraft.endDate === day ? quickDraft.endMin : 1440) - quickDraft.startMin) * PX_PER_MIN)
                    }}
                  >
                    {quickDraft.title || "(sem título)"}
                    <br />
                    {formatTime(quickDraft.startMin)} – {formatTime(quickDraft.endMin)}
                  </div>
                )}

                {day === now.date && (
                  <div className="tg-now" style={{ top: now.minutes * PX_PER_MIN }}>
                    <span className="tg-now-dot" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {holidayPopover.element}
    </div>
  );
}
